// apps/mobile/src/app/services/apiClient.ts
import { AI_SHARING_VERSION } from '../config/aiSharingPolicy';

export function resolveApiUrl(configured: string | undefined, development: boolean): string {
  return configured || (development ? 'http://localhost:8001' : '');
}

export const API_URL = resolveApiUrl(import.meta.env.VITE_API_URL, import.meta.env.DEV);

/** Resolves API-owned uploads returned as relative paths for use in browser media tags. */
export function apiAssetUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${API_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

interface RequestBehavior {
  preserveAuthOnUnauthorized?: boolean;
  expectedStatus?: number;
}

export interface ApiRequestOptions {
  signal?: AbortSignal;
  aiSharingGrantId?: string;
}

let tokenGeneration = 0;
export const getAuthGeneration = () => tokenGeneration;

interface AuthRequestSnapshot {
  token: string | null;
  generation: number;
}

function getToken(): string | null {
  return localStorage.getItem('access_token');
}

function setToken(token: string): void {
  localStorage.setItem('access_token', token);
  tokenGeneration += 1;
}

function clearToken(): void {
  localStorage.removeItem('access_token');
  tokenGeneration += 1;
}

function authHeaders(token = getToken()): HeadersInit {
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    'X-Rubikoo-AI-Sharing-Version': AI_SHARING_VERSION,
  };
}

function captureAuthRequest(): AuthRequestSnapshot {
  return {
    token: getToken(),
    generation: tokenGeneration,
  };
}

function isCapturedAuthCurrent(snapshot: AuthRequestSnapshot): boolean {
  return (
    tokenGeneration === snapshot.generation &&
    getToken() === snapshot.token
  );
}

function throwIfStaleAuthenticatedRequest(
  snapshot: AuthRequestSnapshot,
): void {
  if (snapshot.token && !isCapturedAuthCurrent(snapshot)) {
    throw new DOMException('Account session changed', 'AbortError');
  }
}

async function consumeResponseBody<T>(
  snapshot: AuthRequestSnapshot,
  consume: () => Promise<T>,
): Promise<T> {
  try {
    return await consume();
  } finally {
    throwIfStaleAuthenticatedRequest(snapshot);
  }
}

interface ErrorDetailBody {
  code?: string;
  error?: string;
  module?: string;
  reason?: string;
  message?: string;
  retry_after_seconds?: number;
}

interface ErrorResponseBody {
  code?: string;
  reason?: string;
  message?: string;
  detail?: string | ErrorDetailBody;
  error?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly retryAfterSeconds?: number;
  readonly detail?: ErrorResponseBody;

  constructor(
    message: string,
    options: {
      status: number;
      code?: string;
      retryAfterSeconds?: number;
      detail?: ErrorResponseBody;
    },
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status;
    this.code = options.code;
    this.retryAfterSeconds = options.retryAfterSeconds;
    this.detail = options.detail;
  }
}

function errorMessage(body: ErrorResponseBody, status: number): string {
  if (body.message) return body.message;
  if (typeof body.detail === 'string') return body.detail;
  if (body.detail?.reason) return body.detail.reason;
  if (body.detail?.message) return body.detail.message;
  if (body.reason) return body.reason;
  if (body.error) return body.error;
  return `HTTP ${status}`;
}

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    error.name === 'AbortError'
  );
}

function consumeOptionalJsonErrorBody(
  snapshot: AuthRequestSnapshot,
  response: Response,
): Promise<ErrorResponseBody> {
  return consumeResponseBody(snapshot, async () => {
    try {
      return await response.json() as ErrorResponseBody;
    } catch (error) {
      if (isAbortError(error)) throw error;
      return {};
    }
  });
}

function errorCode(body: ErrorResponseBody): string | undefined {
  if (typeof body.detail === 'object') {
    return body.detail.code ?? body.detail.error ?? body.detail.reason;
  }
  return body.error ?? body.reason;
}

function retryAfterSeconds(response: Response, body: ErrorResponseBody): number | undefined {
  const detailRetry = typeof body.detail === 'object'
    ? body.detail.retry_after_seconds
    : undefined;
  if (typeof detailRetry === 'number' && Number.isFinite(detailRetry) && detailRetry > 0) {
    return Math.ceil(detailRetry);
  }
  const headerRetry = Number.parseInt(response.headers.get('Retry-After') ?? '', 10);
  return Number.isFinite(headerRetry) && headerRetry > 0 ? headerRetry : undefined;
}

async function requestResponse(
  path: string,
  options: RequestInit = {},
  behavior: RequestBehavior = {},
): Promise<{ res: Response; authSnapshot: AuthRequestSnapshot }> {
  const authSnapshot = captureAuthRequest();
  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(authSnapshot.token),
      ...options.headers,
    },
  });

  if (res.status === 401 && !behavior.preserveAuthOnUnauthorized) {
    if (isCapturedAuthCurrent(authSnapshot)) {
      clearToken();
      window.dispatchEvent(new CustomEvent('auth:expired'));
    } else if (authSnapshot.token) {
      throw new DOMException('Account session changed', 'AbortError');
    }
    throw new Error('Session expired');
  }

  throwIfStaleAuthenticatedRequest(authSnapshot);

  if (res.status === 403) {
    const body = await consumeOptionalJsonErrorBody(
      authSnapshot,
      res,
    );
    const reason = body.reason;
    if (isCapturedAuthCurrent(authSnapshot)) {
      if (reason === 'verify_email') {
        window.dispatchEvent(new CustomEvent('auth:verify_required'));
      } else if (reason === 'consent_required') {
        window.dispatchEvent(new CustomEvent('auth:consent_required'));
      } else if (reason === 'profile_required') {
        window.dispatchEvent(new CustomEvent('auth:profile_required'));
      } else if (reason === 'join_required') {
        window.dispatchEvent(new CustomEvent('auth:join_required'));
      }
    }
    throw Object.assign(new Error(body.message ?? `HTTP 403`), { reason, detail: body });
  }

  if (!res.ok) {
    const body = await consumeOptionalJsonErrorBody(
      authSnapshot,
      res,
    );
    if (
      res.status === 409
      && typeof body.detail === 'object'
      && body.detail?.error === 'module_unavailable'
      && isCapturedAuthCurrent(authSnapshot)
    ) {
      window.dispatchEvent(new CustomEvent('event:configuration_changed'));
    }
    throw Object.assign(
      new Error(errorMessage(body, res.status)),
      { code: body.code ?? (typeof body.detail === 'object' ? body.detail?.code : undefined), reason: body.reason, detail: body },
    );
  }

  if (behavior.expectedStatus !== undefined && res.status !== behavior.expectedStatus) {
    throw new Error(`Unexpected response status: ${res.status}`);
  }
  return { res, authSnapshot };
}

async function request<T>(path: string, options: RequestInit = {}, behavior: RequestBehavior = {}): Promise<T> {
  const { res, authSnapshot } = await requestResponse(path, options, behavior);
  if (res.status === 204) return undefined as T;
  return consumeResponseBody(
    authSnapshot,
    () => res.json() as Promise<T>,
  );
}

async function getBlob(
  path: string,
  options: ApiRequestOptions & { maxBytes: number; allowedTypes: readonly string[] },
): Promise<Blob> {
  // Resolve only canonical same-API paths; never let a response URL choose where
  // the bearer header goes. API_URL itself is trusted build configuration.
  let target: URL;
  try {
    if (!path.startsWith('/') || path.startsWith('//') || /[\\\u0000-\u0020]/.test(path)) throw new Error();
    const base = new URL(API_URL);
    target = new URL(`${API_URL}${path}`);
    const expectedPath = base.pathname.replace(/\/$/, '') + path.split('?')[0];
    if (target.origin !== base.origin || target.hash || target.pathname !== expectedPath) throw new Error();
  } catch {
    throw new Error('Invalid private asset path');
  }
  if (!getToken()) throw new Error('Sign in to view private images');
  if (!Number.isSafeInteger(options.maxBytes) || options.maxBytes <= 0 || !options.allowedTypes.length) {
    throw new Error('Invalid image fetch limits');
  }
  const { res, authSnapshot } = await requestResponse(path, {
    signal: options.signal, redirect: 'error', cache: 'no-store', credentials: 'omit',
    headers: { Accept: options.allowedTypes.join(', ') },
  });
  const cancelBody = () => { void res.body?.cancel().catch(() => undefined); };
  if (res.redirected || res.type === 'opaqueredirect' || (res.url && res.url !== target.href)) {
    cancelBody();
    throw new Error('Redirected image response');
  }
  const mediaType = (res.headers.get('Content-Type') ?? '').split(';')[0].trim().toLowerCase();
  if (!options.allowedTypes.includes(mediaType)) {
    cancelBody();
    throw new Error('Unsupported image type');
  }
  const rawLength = res.headers.get('Content-Length');
  const declaredLength = rawLength === null ? null : Number(rawLength);
  if (declaredLength !== null && (!/^\d+$/.test(rawLength!) || !Number.isSafeInteger(declaredLength) || declaredLength > options.maxBytes)) {
    cancelBody();
    throw new Error('Image exceeds size limit');
  }
  if (!res.body) throw new Error('Incomplete image response');
  const reader = res.body.getReader();
  const chunks: ArrayBuffer[] = [];
  let size = 0;
  const checkSession = () => {
    if (options.signal?.aborted) throw new DOMException('Image request aborted', 'AbortError');
    throwIfStaleAuthenticatedRequest(authSnapshot);
  };
  try {
    while (true) {
      checkSession();
      const { done, value } = await consumeResponseBody(authSnapshot, () => reader.read());
      checkSession();
      if (done) break;
      size += value.byteLength;
      if (size > options.maxBytes) throw new Error('Image exceeds size limit');
      chunks.push(new Uint8Array(value).buffer);
    }
    if (!size || (declaredLength !== null && declaredLength !== size)) throw new Error('Incomplete image response');
    checkSession();
    return new Blob(chunks, { type: mediaType });
  } catch (error) {
    await reader.cancel().catch(() => undefined);
    throw error;
  } finally {
    reader.releaseLock();
  }
}

export const apiClient = {
  getBlob,
  get: <T>(path: string, options: ApiRequestOptions = {}) =>
    request<T>(path, { signal: options.signal }),
  post: <T>(path: string, body: unknown, options: ApiRequestOptions = {}) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body), signal: options.signal,
      headers: options.aiSharingGrantId ? { 'X-Rubikoo-AI-Sharing-Grant': options.aiSharingGrantId } : undefined }),
  patch: <T>(path: string, body: unknown, options: ApiRequestOptions = {}) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body), signal: options.signal }),
  put: <T>(path: string, body: unknown, options: ApiRequestOptions = {}) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body), signal: options.signal }),
  delete: <T>(path: string, options: ApiRequestOptions = {}) =>
    request<T>(path, { method: 'DELETE', signal: options.signal }),
  deleteWithBody: (
    path: string,
    body: unknown,
    options: ApiRequestOptions = {},
  ) =>
    request<void>(
      path,
      { method: 'DELETE', body: JSON.stringify(body), signal: options.signal },
      { preserveAuthOnUnauthorized: true, expectedStatus: 204 },
    ),

  /** Upload a file using multipart/form-data. Returns parsed JSON. */
  uploadFile: async <T>(
    path: string,
    formData: FormData,
    options: ApiRequestOptions = {},
  ): Promise<T> => {
    const authSnapshot = captureAuthRequest();
    const headers: HeadersInit = authSnapshot.token
      ? { Authorization: `Bearer ${authSnapshot.token}` }
      : {};
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers,
      body: formData,
      signal: options.signal,
    });
    if (res.status === 401) {
      if (isCapturedAuthCurrent(authSnapshot)) {
        clearToken();
        window.dispatchEvent(new CustomEvent('auth:expired'));
      } else if (authSnapshot.token) {
        throw new DOMException('Account session changed', 'AbortError');
      }
      throw new Error('Session expired');
    }
    throwIfStaleAuthenticatedRequest(authSnapshot);
    if (!res.ok) {
      const body = await consumeOptionalJsonErrorBody(
        authSnapshot,
        res,
      );
      throw Object.assign(
        new Error(errorMessage(body, res.status)),
        { reason: body.reason, detail: body },
      );
    }
    return consumeResponseBody(
      authSnapshot,
      () => res.json() as Promise<T>,
    );
  },

  /** Returns a native fetch Response for SSE streaming */
  stream: async (
    path: string,
    body: unknown,
    options: ApiRequestOptions = {},
  ): Promise<Response> => {
    const authSnapshot = captureAuthRequest();
    const res = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(authSnapshot.token),
        ...(options.aiSharingGrantId ? { 'X-Rubikoo-AI-Sharing-Grant': options.aiSharingGrantId } : {}),
      },
      body: JSON.stringify(body),
      signal: options.signal,
    });
    if (res.status === 401) {
      if (isCapturedAuthCurrent(authSnapshot)) {
        clearToken();
        window.dispatchEvent(new CustomEvent('auth:expired'));
      } else if (authSnapshot.token) {
        throw new DOMException('Account session changed', 'AbortError');
      }
      throw new Error('Session expired');
    }
    throwIfStaleAuthenticatedRequest(authSnapshot);
    if (!res.ok) {
      const errorBody = await consumeOptionalJsonErrorBody(authSnapshot, res);
      throw new ApiError(errorMessage(errorBody, res.status), {
        status: res.status,
        code: errorCode(errorBody),
        retryAfterSeconds: retryAfterSeconds(res, errorBody),
        detail: errorBody,
      });
    }
    return res;
  },

  setToken,
  getToken,
  clearToken,
};

export default apiClient;
